import { useEffect, useRef, useState } from "react";
import { aiCategoryLabels, validateDraft, type AiDraft } from "./ai-intake.js";
import "./ai-intake.css";

export function AiNotes({ draft }: { draft: AiDraft }): React.JSX.Element {
  return <ol className="ai-notes">{draft.items.map((item) => <li key={item.id}><span>{aiCategoryLabels[item.category]}</span><p className="intake-verbatim">{item.text}</p></li>)}</ol>;
}

export function AiIntakeReview({ situation, onChange }: { situation: string; onChange: (draft: AiDraft | null, blocked: boolean) => void }): React.JSX.Element {
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<AiDraft | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [error, setError] = useState(false);
  const current = useRef<AbortController | null>(null);
  useEffect(() => () => { current.current?.abort(); current.current = null; }, [situation]);

  function discard(): void {
    current.current?.abort(); current.current = null;
    setBusy(false); setDraft(null); setReviewed(false); setConsent(false); setError(false); onChange(null, false);
  }
  async function organize(): Promise<void> {
    if (!consent || current.current !== null) return;
    const controller = new AbortController();
    current.current = controller;
    const timer = setTimeout(() => controller.abort(), 8000);
    setBusy(true); setError(false); setDraft(null); setReviewed(false); onChange(null, true);
    try {
      const response = await fetch("/api/v1/ai/intake", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ situation, aiConsent: true }), signal: controller.signal, cache: "no-store", credentials: "same-origin", redirect: "error" });
      if (!response.ok) throw new Error("AI_UNAVAILABLE");
      const result = validateDraft(await response.json(), situation);
      if (current.current !== controller) return;
      controller.signal.throwIfAborted();
      setDraft(result);
    } catch {
      if (current.current !== controller) return;
      setError(true); onChange(null, false);
    } finally {
      clearTimeout(timer);
      if (current.current === controller) { current.current = null; setBusy(false); }
    }
  }
  return <section className="ai-intake" aria-labelledby="ai-intake-title" aria-busy={busy}>
    <div className="ai-intake-heading"><span>선택 기능 · AI 정리</span><h3 id="ai-intake-title">전달할 내용을 항목별로 나눠 볼까요?</h3></div>
    <p>AI는 원문을 증상·시점·복용약 등으로 분류합니다. 문장을 줄이거나 진단하지 않으며, 없는 정보는 채우지 않습니다.</p>
    {draft === null && !busy && <><p className="privacy-note" id="ai-consent-note">선택하면 ‘현재 상황’을 병원사전 서버를 거쳐 외부 AI 서버로 보내 정리합니다. AI 요청만으로 내 계정에 기록이 저장되지는 않습니다. 외부 AI 제공자의 처리·보관 조건이 적용됩니다. 이름·연락처는 적지 마세요.</p><label className="intake-check"><input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} aria-describedby="ai-consent-note"/>현재 상황을 외부 AI 서버로 보내 정리하는 데 동의합니다.</label><button type="button" className="situation-clear" disabled={!consent} onClick={() => { void organize(); }}>AI로 항목 나누기</button></>}
    {busy && <p role="status">원문을 정리하고 있어요. 최대 8초 정도 걸립니다.</p>}
    {error && <p role="status" className="intake-error">AI 정리를 완료하지 못했어요. 직접 입력한 내용은 그대로 남아 있으니 확인 후 계속 진행하세요.</p>}
    {draft !== null && <><p className="ai-review-notice" role="status">AI 초안입니다. 각 문장과 분류를 확인해 주세요. 맞지 않으면 직접 입력으로 계속할 수 있습니다.</p><AiNotes draft={draft}/><label className="intake-check"><input type="checkbox" checked={reviewed} onChange={(event) => { setReviewed(event.target.checked); onChange(event.target.checked ? draft : null, !event.target.checked); }}/>AI 분류와 원문을 대조해 직접 확인했습니다.</label></>}
    {(busy || draft !== null) && <button type="button" className="situation-clear" onClick={discard}>AI 정리 제외하고 직접 입력으로 계속</button>}
  </section>;
}
